import React, { useEffect, useId, useRef, useState } from 'react';
import { listChurchNames } from '../lib/api/churches';
import { captureError } from '../lib/capture-error';

interface ChurchNameInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const ChurchNameInput: React.FC<ChurchNameInputProps> = ({
  value,
  onChange,
  id,
  className = '',
  error,
  required = false,
  disabled = false,
  placeholder = 'Start typing your church name',
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const listId = `${inputId}-suggestions`;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await listChurchNames(value.trim(), 15);
        setSuggestions(results.map((r) => r.name));
      } catch (err) {
        captureError(err, 'Failed to load church name suggestions');
        setSuggestions([]);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  return (
    <div className="church-name-input">
      <input
        type="text"
        id={inputId}
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${className}${error ? ' error' : ''}`}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="organization"
      />
      <datalist id={listId}>
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {error && <span className="error-text">{error}</span>}
    </div>
  );
};

export default ChurchNameInput;
